import React, { useCallback, useEffect, useState } from "react";
import { App, Card, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { HistoryOutlined } from "@ant-design/icons";
import type { IStockMovement } from "../../interfaces/stock";
import { stockService } from "../../api/stockService";
import { PageHeader } from "../../components/ui";
import { extractApiError } from "../../utils/apiErrors";

export const StockAdjustments: React.FC = () => {
    const { message } = App.useApp();
    const [rows, setRows] = useState<IStockMovement[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await stockService.getRecentStockMovements(100);
            setRows(Array.isArray(data) ? data : []);
        } catch (e: unknown) {
            setRows([]);
            message.error(extractApiError(e, "Could not load recent adjustments."));
        } finally {
            setLoading(false);
        }
    }, [message]);

    useEffect(() => {
        void load();
    }, [load]);

    const columns: ColumnsType<IStockMovement> = [
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (v: string) => new Date(v).toLocaleString(),
            width: 180,
        },
        {
            title: "Code",
            dataIndex: "productCode",
            key: "productCode",
            width: 120,
        },
        {
            title: "Product",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Change",
            dataIndex: "quantity",
            key: "quantity",
            render: (v: number) => (
                <Tag color={v >= 0 ? "blue" : "red"}>
                    {v >= 0 ? "+" : ""}
                    {Number(v).toFixed(2)}
                </Tag>
            ),
            width: 100,
        },
        {
            title: "Balance after",
            dataIndex: "balanceAfter",
            key: "balanceAfter",
            render: (v: number) => Number(v).toFixed(2),
            width: 120,
        },
        { title: "Reason / notes", dataIndex: "notes", key: "notes", ellipsis: true },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Stock adjustments"
                titleIcon={<HistoryOutlined />}
                breadcrumbs={[
                    { title: "Stock", href: "/stock" },
                    { title: "Adjustments" },
                ]}
                actions={[
                    {
                        label: "Stock levels",
                        href: "/stock",
                        type: "default",
                        variant: "outlined",
                    },
                ]}
            />

            <Card>
                <Table<IStockMovement>
                    rowKey="id"
                    loading={loading}
                    dataSource={rows}
                    columns={columns}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    locale={{ emptyText: "No adjustments recorded yet." }}
                />
            </Card>
        </div>
    );
};

export default StockAdjustments;
